// FilePreviewList.jsx
// This component is used to display a preview of images in the file viewer.
// It supports lazy loading of images and allows for editing additional names and comments.
// It can also be used in an edit mode where users can modify file information.

// Copyright HS Analysis GmbH, 2022
// Author: Jakob Burghardt, Viktor Eberhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";

// MUI imports
import { makeStyles } from "@mui/styles";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { Tooltip } from "@mui/material";

// HSA Imports
import Backend from "../../common/utils/Backend";
import SceneImagePreview from "./SceneImagePreview";
import { FileViewerContext } from "../FileViewer";
import { CaseContext } from "../../cases/components/CaseTableDockSideBar";
import SlidePreview from "../../cases/components/SlidePreview";

const useStyles = makeStyles((theme) => ({
  root: {
    minWidth: 0,
    background: "#fff",
    overflow: "hidden",
    justifyContent: "center",
    height: "100%",
    display: "grid",
    gridTemplateRows: "auto 1fr",
  },
  filterInputContainer: {
    margin: 6,
  },
  imgBox: {
    overflow: "auto",
    height: "100%",
  },
  itemContainer: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    textAlign: "center",
    gap: 10,
    paddingTop: 10,
  },
  inputContainer: {
    display: "grid",
    width: "100%",
    height: "100%",
    gridTemplateRows: "auto 1fr",
  },
  imgContainer: {
    margin: 5,
    textAlign: "center",
    alignItems: "center",
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
    "> div >div": {
      border: "3px solid " + theme.palette.primary.inacitve,
    },
    "&:hover >div >div:last-child": {
      backgroundColor: theme.palette.selected.main,
    },
    // Add touch interaction styles
    "@media (hover: none)": {
      "&:active >div >div:last-child": {
        backgroundColor: theme.palette.selected.main,
      },
    },
  },
  imgActive: {
    display: "inline-block",
    cursor: "pointer",
    width: "100%",
    maxHeight: "200px",
    border: `solid 3px ${theme.palette.primary.main}`,
    objectFit: "contain",
  },
  imgSplitScreen: {
    display: "inline-block",
    cursor: "pointer",
    width: "100%",
    maxHeight: "200px",
    border: `dashed 3px ${theme.palette.primary.main}`,
    objectFit: "contain",
  },
  placeholder: {
    height: 200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  lazyImage: {
    objectFit: "contain",
    width: "100%",
  },
  infoLabel: {
    display: "flex",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "15px",
    color: "#808080",
  },
  ellipsisText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "block",
    alignItems: "center",
    maxWidth: "150px",
    position: "relative",
    paddingRight: "16px",
  },
  infoIcon: {
    position: "absolute",
    right: 0,
    top: 2,
    marginLeft: 4,
    fontSize: 16,
    color: "#888",
    padding: 0,
    pointerEvents: "auto",
  },
  previewContent: {
    position: "relative",
  },
  hasSeenDot: {
    pointerEvents: "auto",
    zIndex: 1000,
    position: "absolute",
    top: 8,
    right: 8,
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: "#4caf50",
  },
}));

/**
 * Preview from the loaded images of the selected Foldertree
 */
const FilePreviewList = (props) => {
  const FV =
    React.useContext(FileViewerContext) || React.useContext(CaseContext);
  // Prefer props, fallback to context
  const {
    sceneDataList,
    setSceneDataList,
    activeFileId,
    splitscreenFileIds = [],
    setActiveFileId = null,
    setActiveFilePath = null,
    showAdditionalName = true,
    showComments = true,
    selCase = null,
    // containerWidth = props.containerWidth || 480,
  } = FV;
  const { editMode } = props;
  const classes = useStyles();
  const history = useHistory();
  const labelHeight = 30;
  const [missingThumbnails, setMissingThumbnails] = React.useState({});
  const imageContainerRef = React.useRef(null);
  const imageRefs = React.useRef([]);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogText, setDialogText] = React.useState("");
  const [dialogTitle, setDialogTitle] = React.useState("");
  const [filterText, setFilterText] = React.useState("");

  React.useEffect(() => {
    if (sceneDataList.length > 0 && activeFileId) {
      const timeout = setTimeout(() => {
        scrollToActiveFile();
      }, 0);

      return () => clearTimeout(timeout);
    }
    // Only depend on sceneDataList length
  }, [sceneDataList.length]);

  React.useEffect(() => {
    calculatePreviewHeight();
  }, []);

  /**
   * Handles the error event when an image fails to load for a given file.
   * Updates the `missingThumbnails` state to mark the file's thumbnail as missing.
   *
   * @param {Object} file - The file object whose image failed to load.
   * @param {string|number} file.id - The unique identifier of the file.
   */
  const handleImageError = (file) => {
    const sceneIdx = sceneDataList.findIndex(
      (sceneItem) => sceneItem.id === file.id
    );
    if (sceneIdx === -1) return;
    let mT = {
      ...missingThumbnails,
      [sceneDataList[sceneIdx].id]: true,
    };
    setMissingThumbnails(mT);
  };

  /**
   * Determines if the content of a given HTML element is truncated horizontally.
   *
   * @param {HTMLElement|null} el - The HTML element to check for truncation.
   * @returns {boolean} True if the element's scroll width exceeds its client width, indicating truncation; otherwise, false.
   */
  const isTruncated = (el) => {
    if (!el) return false;
    return el.scrollWidth > el.clientWidth;
  };

  /**
   * Scrolls the image preview list to bring the currently active file into view.
   * Finds the index of the active file in the scene data list, and if found,
   * scrolls the corresponding image element into view at the top of the container.
   * Uses a timeout to ensure the DOM is updated before scrolling.
   */
  const scrollToActiveFile = () => {
    if (activeFileId) {
      const sceneIdx = sceneDataList.findIndex(
        (file) => file.id === activeFileId
      );
      if (sceneIdx === -1) return;
      setTimeout(() => {
        const targetElement = imageRefs.current[sceneIdx];
        const container = imageContainerRef.current;
        if (targetElement && container) {
          targetElement.scrollIntoView({
            behavior: "auto",
            block: "start",
          });
          // Adjust scrollTop for an offset
          container.scrollTop = container.scrollTop - 80;
        }
      }, 0);
    }
  };

  /**
   * Calculates and updates the height property for each item in the sceneDataList array.
   * For each image reference in imageRefs, if the element exists and has a positive clientHeight,
   * sets the corresponding sceneDataList item's height to the sum of the image's height and labelHeight.
   */
  const calculatePreviewHeight = () => {
    for (let i = 0; i < sceneDataList.length; i++) {
      let element1 = imageRefs.current[i];
      if (element1) {
        const elemHeight = element1.clientHeight;
        if (elemHeight > 0) {
          sceneDataList[i].height = elemHeight + labelHeight;
        }
      }
    }
  };

  /**
   * Saves an additional name for a given scene if it has changed.
   * @param {Rect.ChangeEvent<HTMLInputElement} e - The change event from the input field.
   * @param {Object} scene - The scene object containing the additional name and id.
   * @param {string|number} scene.id - The unique identifier for the scene.
   * @param {string} [scene.additionalName] - The current additional name associated with
   */
  const saveAdditionalName = (e, scene) => {
    if (e.target.value === scene.additionalName) return;

    Backend.saveFileIdAdditionalName(scene.id, e.target.value)
      .then(() => {
        window.showSuccessSnackbar("Additional name saved");
        setSceneDataList((prev) => {
          return prev.map((s) => {
            if (s.id === scene.id) {
              return { ...s, additionalName: e.target.value };
            }
            return s;
          });
        });
      })
      .catch((error) => {
        window.openErrorDialog(
          "Error while saving additional name:",
          error.message
        );
      });
  };

  /**
   * Saves a comment for a given scene if it has changed.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event from the input field.
   * @param {Object} scene - The scene object containing the comment and id.
   * @param {string|number} scene.id - The unique identifier for the scene.
   * @param {string} [scene.comment] - The current comment associated with the scene.
   */
  const saveComment = (e, scene) => {
    if (e.target.value === scene.comment) return;

    Backend.saveFileIdComment(scene.id, e.target.value)
      .then(() => {
        window.showSuccessSnackbar("Additional name saved");

        setSceneDataList((prev) => {
          return prev.map((s) => {
            if (s.id === scene.id) {
              return { ...s, comment: e.target.value };
            }
            return s;
          });
        });
      })
      .catch((error) => {
        window.openErrorDialog("Error while saving comment:", error.message);
      });
  };

  // Filter sceneDataList by file name (case-insensitive)
  const filteredSceneDataList = React.useMemo(
    () =>
      sceneDataList.filter(
        (scene) =>
          !filterText ||
          (scene.name &&
            scene.name.toLowerCase().includes(filterText.toLowerCase()))
      ),
    [sceneDataList, filterText]
  );

  if (!sceneDataList || sceneDataList.length === 0) {
    return null;
  }

  return (
    <div className={classes.root}>
      <div className={classes.filterInputContainer}>
        <TextField
          label="Filter by file name"
          variant="outlined"
          size="small"
          fullWidth
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
      </div>
      {filteredSceneDataList.length > 0 ? (
        <div
          className={classes.imgBox}
          ref={(el) => (imageContainerRef.current = el)}
        >
          {filteredSceneDataList.map((scene, idx) => {
            let fileStatus = "img";
            if (activeFileId === scene.id) {
              fileStatus = "imgActive";
            } else if (splitscreenFileIds.includes(scene.id)) {
              fileStatus = "imgSplitScreen";
            }
            return (
              <div
                key={scene.path + scene.id}
                className={
                  editMode ? classes.itemContainer : classes.imgContainer
                } // Use a container class for grouping
                ref={(el) => (imageRefs.current[idx] = el)}
                draggable={!editMode && !selCase}
                onDragStart={(e) => {
                  e.dataTransfer.setData("fileId", scene.id);
                }}
                onClick={() => {
                  if (editMode) return;
                  if (setActiveFileId && setActiveFilePath) {
                    setActiveFileId(scene.id);
                    setActiveFilePath(scene.path);
                  }
                  if (selCase && selCase.Id) {
                    history.push(`/file_view/${selCase.Id}?fileId=${scene.id}`);
                  }
                }}
              >
                <div className={classes.previewContent}>
                  {scene.hasSeen && (
                    <Tooltip
                      disableInteractive
                      placement="top"
                      title={"This file has been seen before!"}
                    >
                      <div className={classes.hasSeenDot}></div>
                    </Tooltip>
                  )}
                  {selCase ? (
                    <SlidePreview
                      showAdditionalName={showAdditionalName}
                      showComments={showComments}
                      fileStatus={fileStatus}
                      file={scene}
                      containerWidth={500}
                    />
                  ) : (
                    <SceneImagePreview
                      scene={scene}
                      showAdditionalName={showAdditionalName}
                      showComments={showComments}
                      fileStatus={fileStatus}
                      width={editMode ? "200px" : ""}
                      isTruncated={isTruncated}
                      setDialogTitle={setDialogTitle}
                      setDialogText={setDialogText}
                      setDialogOpen={setDialogOpen}
                      handleImageError={handleImageError}
                    />
                  )}
                </div>

                {editMode && (
                  <div className={classes.inputContainer}>
                    <TextField
                      label="Additional Name"
                      variant="outlined"
                      size="small"
                      fullWidth
                      style={{ marginBottom: 8 }}
                      defaultValue={scene.additionalName || ""}
                      onBlur={(e) => saveAdditionalName(e, scene)}
                    />
                    <TextField
                      label="Comment"
                      variant="outlined"
                      size="small"
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={8}
                      InputProps={{
                        style: { height: "100%", alignItems: "flex-start" },
                      }}
                      style={{ flex: 1 }}
                      defaultValue={scene.comment || ""}
                      onBlur={(e) => saveComment(e, scene)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className={classes.infoLabel}>No Files or CASES</div>
      )}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <div style={{ whiteSpace: "pre-wrap" }}>{dialogText}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

FilePreviewList.propTypes = {
  // Optional props
  editMode: PropTypes.bool,
};

// defautl props
FilePreviewList.defaultProps = {
  editMode: false,
};

export default FilePreviewList;
